import React from 'react';
import styled from 'styled-components';
import { TStyledTheme } from 'theme';
import { EColors } from 'types/themenum';

export const statusColors = {
    total: EColors.totalAttendee,
    'not invited': EColors.notInvitedAttendee,
    'not sent': EColors.notInvitedAttendee, // Related to the attendee status being enumerable on the database; this is a workaround
    invited: EColors.invitedAttendee,
    attending: EColors.attendingAttendee,
    'not attending': EColors.notAttendingAttendee,
    roomBlocks: EColors.roomBlocks,

    // Survey Response Status
    sent: EColors.surveysSentTotal,
    pending: EColors.surveysPending,
    responded: EColors.surveysRespondedTo,
};

export const statusMap: Record<BizlyAPI.AttendeeStatus, string> = {
    'not sent': 'Not Sent',
    invited: 'Invited',
    attending: 'Attending',
    'not attending': 'Declined',
    'not invited': 'Not Invited',
};

export const statusOptions = Object.entries(statusMap).map(([status, statusLabel]) => ({
    id: status,
    label: statusLabel,
}));

type AttendanceTypeMap = {
    'In-Person': 'In-Person';
    Virtual: 'Virtual';
    [key: string]: string;
};

export const attendanceTypeMap: AttendanceTypeMap = {
    'In-Person': 'In-Person',
    Virtual: 'Virtual',
};

export const attendanceTypeOptions = Object.keys(attendanceTypeMap).map(attendanceType => ({
    id: attendanceType,
    label: attendanceTypeMap[attendanceType],
}));

type IconStyleProps = {
    inverted?: boolean;
    colorOverride?: EColors;
};

const iconStyledFn = ({
    inverted,
    colorOverride,
    theme: { getColor, EColors },
}: IconStyleProps & { theme: TStyledTheme }) => ({
    color: getColor(
        colorOverride ? colorOverride : inverted ? EColors.invertedDisplayIcon : EColors.primaryDisplayIcon
    ),
    '> path': {
        fill: getColor(
            colorOverride ? colorOverride : inverted ? EColors.invertedDisplayIcon : EColors.primaryDisplayIcon
        ),
    },
});

export type InteractibleIconStyleProps = {
    inverted?: boolean;
    warning?: boolean;
};

const interactibleIconStyledFn = ({
    inverted,
    warning,
    theme,
}: InteractibleIconStyleProps & { theme: TStyledTheme }) => ({
    ...iconStyledFn({ inverted, theme }),
    ...(!inverted
        ? {
              color: theme.getColor(theme.EColors.primaryAction),
              '> path': {
                  fill: theme.getColor(theme.EColors.primaryAction),
              },
              '&:hover': {
                  color: theme.getColor(theme.EColors.primaryActionHover),
                  '> path': {
                      fill: theme.getColor(theme.EColors.primaryActionHover),
                  },
              },
          }
        : {}),
    ...(warning && {
        color: theme.getColor(theme.EColors.warningAction),
        '> path': {
            fill: theme.getColor(theme.EColors.warningAction),
        },
        '&:hover': {
            color: theme.getColor(theme.EColors.warningActionHover),
            '> path': {
                fill: theme.getColor(theme.EColors.warningActionHover),
            },
        },
    }),
    cursor: 'pointer',
});

export const withIconStyles = <P extends object, T extends React.ComponentType<P>>(icon: T) =>
    styled(icon)<IconStyleProps>(iconStyledFn);

export const withInteractibleIconStyles = <P extends object, T extends React.ComponentType<P>>(icon: T) =>
    styled(icon)<InteractibleIconStyleProps>(interactibleIconStyledFn);

export const contentWidth = '700px';

type CopyFontSizes = {
    xlarge: string;
    large: string;
    default: string;
    regular: string;
    small: string;
    xsmall: string;
    [key: string]: string;
};

export const copyFontSizes: CopyFontSizes = {
    xlarge: '20px',
    large: '18px',
    default: '15px',
    regular: '15px',
    small: '13px',
    xsmall: '11px',
};

export const OnlySingleLine = `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ROLE_OPTIONS = [
    { id: 'Administration', name: 'Administration' },
    { id: 'Customer & Support', name: 'Customer & Support' },
    { id: 'Engineering & IT', name: 'Engineering & IT' },
    { id: 'Executive', name: 'Executive' },
    { id: 'Finance & Legal', name: 'Finance & Legal' },
    { id: 'Human Resources', name: 'Human Resources' },
    { id: 'Operations & Facilities', name: 'Operations & Facilities' },
    { id: 'Product & Design', name: 'Product & Design' },
    { id: 'Sales & Marketing', name: 'Sales & Marketing' },
    { id: 'Other', name: 'Other' },
] as const;

export const DEPARTMENT_OPTIONS = [
    { id: 'Activities & Entertainment', name: 'Activities & Entertainment' },
    { id: 'Administrative', name: 'Administrative' },
    { id: 'Bar & Beverage', name: 'Bar & Beverage' },
    { id: 'Catering & Banquets', name: 'Catering & Banquets' },
    { id: 'Culinary', name: 'Culinary' },
    { id: 'Design & Production', name: 'Design & Production' },
    { id: 'Events', name: 'Events' },
    { id: 'Facilities & Maintenance', name: 'Facilities & Maintenance' },
    { id: 'Finance', name: 'Finance' },
    { id: 'Front Office & Guest Services', name: 'Front Office & Guest Services' },
    { id: 'Marketing & Sales', name: 'Marketing & Sales' },
    { id: 'Reservations & Booking', name: 'Reservations & Booking' },
    { id: 'Spa & Wellness', name: 'Spa & Wellness' },
    { id: 'Venue Management', name: 'Venue Management' },
    { id: 'Other', name: 'Other' },
] as const;

export const BIZLYOS_CONTACT_TYPES = [
    { id: 'Primary', name: 'Primary' },
    { id: 'Secondary', name: 'Secondary' },
    { id: 'Finance', name: 'Finance' },
    { id: 'General Manager', name: 'General Manager' },
    { id: 'Catering', name: 'Catering' },
    { id: 'Guest Client Services', name: 'Guest / Client Services' },
] as const;

export const AttendeeStatus: Record<string, BizlyAPI.AttendeeStatus> = {
    notInvited: 'not invited',
    notSent: 'not sent',
    invited: 'invited',
    attending: 'attending',
    notAttending: 'not attending',
} as const;

export const PostHogFeatureFlags = {
    toggleNewCompareProposals: 'toggleNewCompareProposals',
    toggleNewEventNav: 'toggleNewEventNav',
    dashboardPage: 'dashboardPage',
    aiPackagesEnabled: 'aiPackagesEnabled',
    autoRebidEnabled: 'autoRebidEnabled',
} as const;

export const BizlyOsPostHogFeatureFlags = {
    toggleSettings: 'toggleSettings',
    toggleVenueContactInfo: 'toggleVenueContactInfo',
    toggleCreateListing: 'toggleCreateListing',
} as const;
